/**
 * TripReportCard — compact card for recent trip report list.
 *
 * Shows: account name, date, visit type icon, first topic summary,
 * sentiment badge. Taps navigate to the detail view.
 */
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Phone,
  Mail,
  MapPin,
  HelpCircle,
  Calendar,
  Pencil,
} from "lucide-react";
import { cn } from "../lib/utils";
import { formatDate as formatDateBase } from "../../utils/dateFormatters";
import type { TripReport, Sentiment, VisitType } from "../types";

interface TripReportCardProps {
  report: TripReport;
  /** Optional edit handler. When provided, shows an edit button on the card. */
  onEdit?: () => void;
}

const VISIT_TYPE_ICON: Record<VisitType, typeof Building2> = {
  in_person: MapPin,
  phone_call: Phone,
  email: Mail,
  other: HelpCircle,
};

const VISIT_TYPE_LABEL: Record<VisitType, string> = {
  in_person: "In Person",
  phone_call: "Phone",
  email: "Email",
  other: "Other",
};

const SENTIMENT_STYLES: Record<Sentiment, string> = {
  positive: "bg-success/15 text-success",
  negative: "bg-error/15 text-error",
  neutral: "bg-surface-inset text-text-muted",
  mixed: "bg-warning/15 text-warning",
};

const SENTIMENT_LABEL: Record<Sentiment, string> = {
  positive: "Positive",
  negative: "Negative",
  neutral: "Neutral",
  mixed: "Mixed",
};

// Local wrapper to handle date-only strings (YYYY-MM-DD)
function formatDate(dateStr: string | null): string {
  if (!dateStr) return "No date";
  try {
    // If it's a date-only string, append time to avoid timezone issues
    const dateToFormat = dateStr.includes('T') ? dateStr : dateStr + "T00:00:00";
    return formatDateBase(dateToFormat);
  } catch {
    return dateStr;
  }
}

function getFirstTopicSummary(report: TripReport): string | null {
  if (!report.topics || report.topics.length === 0) return null;
  const topic = report.topics[0];
  const text = topic.detail || topic.subject;
  if (text.length > 80) return text.slice(0, 77) + "...";
  return text;
}

export function TripReportCard({ report, onEdit }: TripReportCardProps) {
  const navigate = useNavigate();
  const VisitIcon = VISIT_TYPE_ICON[report.visit_type] ?? HelpCircle;
  const sentiment = report.overall_sentiment as Sentiment;
  const topicSummary = getFirstTopicSummary(report);

  return (
    <button
      type="button"
      onClick={() => navigate(`/copilot/report/${report.id}`)}
      className={cn(
        "flex w-full flex-col gap-2 rounded-xl border border-border",
        "bg-surface-elevated p-3 text-left",
        "transition-colors active:bg-surface-inset"
      )}
    >
      {/* Top row: account name + sentiment */}
      <div className="flex items-start justify-between gap-2">
        <h3 className="flex-1 text-sm font-semibold text-text-primary leading-snug">
          {report.account_name ?? "Unknown Account"}
        </h3>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
            SENTIMENT_STYLES[sentiment]
          )}
        >
          {SENTIMENT_LABEL[sentiment]}
        </span>
      </div>

      {/* Meta row: date + visit type */}
      <div className="flex items-center gap-3 text-xs text-text-muted">
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          {formatDate(report.visit_date)}
        </span>
        <span className="flex items-center gap-1">
          <VisitIcon className="h-3.5 w-3.5" />
          {VISIT_TYPE_LABEL[report.visit_type]}
        </span>
      </div>

      {/* Topic summary */}
      {topicSummary && (
        <p className="text-xs text-text-secondary leading-relaxed line-clamp-2">
          {topicSummary}
        </p>
      )}

      {/* Edit button — shown when onEdit is provided */}
      {onEdit && (
        <div
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.stopPropagation();
              onEdit();
            }
          }}
          className="flex items-center gap-1 self-end rounded-lg bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand active:bg-brand/20"
        >
          <Pencil className="h-3 w-3" />
          Edit Report
        </div>
      )}
    </button>
  );
}
